import React, { useEffect, useState } from "react";
import classNames from "classnames";

// Models
import { IFoodOrder } from "api/rpc/2024-04/facilityAdmin/facility/foodOrdering";

// CSS
import { faArrowUp, faClockRotateLeft, faExclamationTriangle } from "@fortawesome/pro-regular-svg-icons";
import "./FoodOrderChit.scss";

// Helpers
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { capitalize, capitalizeWords } from "helpers/Helpers";
import moment, { MomentInput } from "moment";
import { ButtonNew } from "components/buttonNew";
import Divider from "components/divider";

type FoodOrderChitProps = {
  order?: IFoodOrder;
  key: number;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  elevateStatus?: (e: React.MouseEvent<HTMLElement>, CHIT_STATUS: IChitStatus, order: IFoodOrder) => void;
  markAsOvertime?: boolean;
  markAsOvertimeMinutes?: number;
};

interface IChitStatus {
  id: number;
  msg: string;
  elevated: string;
}
export default function FoodOrderChit(props: FoodOrderChitProps) {
  const { order, onClick, markAsOvertime, markAsOvertimeMinutes } = props;
  const prefixClass = "food-order-chit-status-bar";

  const [timer, setTimer] = useState(null);

  const CHIT_STATUS = [
    { id: 1, msg: "new", elevated: "preparing" },
    { id: 2, msg: "preparing", elevated: "done" },
    { id: 3, msg: "done", elevated: "archived" },
    { id: 4, msg: "archived", elevated: "void" },
    { id: 5, msg: "void", elevated: "new" },
    { id: 10, msg: null, elevated: "new" },
  ];
  const currStatus = CHIT_STATUS.find(stat => stat.msg === order?.status?.toLowerCase());
  const chitCls = classNames(prefixClass, {
    [`${prefixClass}-received`]: currStatus?.id === 0,
    [`${prefixClass}-new`]: currStatus?.id === 1,
    [`${prefixClass}-preparing`]: currStatus?.id === 2,
    [`${prefixClass}-done`]: currStatus?.id === 3,
    [`${prefixClass}-served`]: currStatus?.id === 4,
    [`${prefixClass}-void`]: currStatus?.id === 5,
  });

  useEffect(() => {
    let interval: NodeJS.Timeout = null;
    function formatTime(timeDifference: moment.Duration) {
      const hours = timeDifference.hours().toString().padStart(2, "0");
      const minutes = timeDifference.minutes().toString().padStart(2, "0");
      const seconds = timeDifference.seconds().toString().padStart(2, "0");
      if (hours === "0" || hours === "00") {
        return `${minutes}:${seconds}`;
      } else {
        return `${hours}:${minutes}:${seconds}`;
      }
    }

    interval = setInterval(() => {
      const currentTime = moment();
      //Get the time the chit was sent to the kitchen in user's local time
      const chitSentTime = moment.utc(order?.sent_at).local();

      //Get the difference in current time & chit sent time
      const differenceTime = moment.duration(currentTime.diff(chitSentTime));
      const formattedElapsedTime = formatTime(differenceTime);

      setTimer(formattedElapsedTime);
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <>
      <div className="food-order-chit-container" onClick={onClick}>
        <div className={chitCls}>
          <div className="food-order-chit-header">
            {/* Order # (?)*/}
            <div className="food-order-chit-header-order">{order?.order?.name}</div>

            {/* Order Status (Preparing, New, Overdue) */}
            <div className="food-order-chit-header-status">{capitalizeWords(order?.status)}</div>

            {/* Elevate Order State (Preparing, New, Overdue) */}
            <ButtonNew type="text" onClick={e => props.elevateStatus(e, currStatus, order)}>
              <FontAwesomeIcon icon={faArrowUp} size="lg" color="#667085" fixedWidth />
            </ButtonNew>
          </div>

          <Divider style={{ margin: 0 }} />
          <div className="food-order-chit-timer-and-title">
            {/* Order Location */}
            <div className="food-order-chit-timer-and-title-title">
              {order?.order?.pickup_location?.name ? capitalize(order?.order?.pickup_location?.name) : null}
            </div>

            {/* Order Duration */}
            <div className="food-order-chit-timer-and-title-timer">
              {/* OVERTIME */}
              {markAsOvertime && moment(timer as MomentInput, ["HH:mm:ss"]).isAfter(moment(markAsOvertimeMinutes, ["HH:mm:ss"])) ? (
                <FontAwesomeIcon icon={faExclamationTriangle} beat className="text-error pr-2" />
              ) : null}
              <FontAwesomeIcon icon={faClockRotateLeft} size="sm" className="pr-2" />
              <div>
              {timer}

              </div>
            </div>
          </div>

          <div className="food-order-chit-customer-header">
            {/* Order # */}
            {/* <div className="food-order-chit-header-info">{order?.order?.number}</div> */}

            {/* Customer Name */}
            <div className="food-order-chit-header-info">
              {order?.user_full_name ? capitalizeWords(order?.order?.customer?.full_name) : ""}
            </div>
          </div>
         
        </div>

        {order?.order?.notes ? <div className="order-note-bar">{order?.order?.notes}</div> : null}
        {order?.order?.financial_status === "paid" ? <div className="paid-status-bar">**Paid**</div> : null}
        <div className="food-order">
          {/* Iterate and display food item */}
          {order?.line_items.map((lineItem, lineItemIndex) => {
            return (
              <div key={lineItemIndex}>
                <div className="flex flex-col pb-2 pl-3 pt-2">
                  <span>
                    {lineItem.quantity} {lineItem?.product_title}
                  </span>

                  {lineItem?.children_line_items.length > 0
                    ? lineItem?.children_line_items?.map((modifier, modifierIndex) => {
                        return (
                          <React.Fragment key={modifierIndex}>
                            <div className="food-order-modifier">{modifier.product_title}</div>
                          </React.Fragment>
                        );
                      })
                    : null}

                  <span className="font-400 text-12 ml-2">{lineItem?.note ? lineItem?.note : null}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
